/**
 * useItemQueryWorkspace — the state behind the one shared item list block
 * (ItemQueryWorkspace): the query (statement bar and filters), the columns,
 * the filter panel, and the rows that come out of them.
 *
 * The item list, the Report Designer and the Item Pool Editor all draw their
 * list through it (roadmap N-16, N-19), so a query, a filter or a column
 * behaves the same on all three. What differs is said here as options:
 *
 *   - Where the statement lives. The item list keeps it on the address
 *     (`pql`); a report and a pool keep their own (syncUrl off).
 *   - The filters it opens with. The item list opens on Status = Active; a
 *     pool opens on every member.
 *   - The columns. The person's own in this browser (the item list's, or a
 *     pool's under its own key), or a set the host owns (a report's, saved
 *     with the report).
 *   - Rows the host narrows to after the query (a report's date range).
 *   - Ranked: the items keep the order they are given (a pool's rank). No
 *     sort, no grouping, and no collapsing a settled cluster into one entry —
 *     every member keeps its own place.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Item } from '../../../../types/prioritization/lifecycle';
import { useEnterpriseIdentityStore } from '../../../../stores/prioritization/enterpriseIdentityStore';
import { useItemListState, useAllColumns, type FilterGroup, type SortSpec } from './useItemListState';
import { useItemListData, type ItemRow } from './useItemListData';
import { useStatementQuery } from './useStatementQuery';
import { useClusterRowSelection } from './useClusterRowSelection';

export type ItemQueryColumns =
  /** The person's column choice in this browser, kept under this key. */
  | { storageKey: string; defaults: string[] }
  /** A set the host owns and saves (a report's). */
  | { visible: string[]; setVisible: (cols: string[]) => void };

export interface ItemQueryWorkspaceOptions {
  /** What the list is over: the workspace's items, or a pool's members in rank order. */
  items: Item[];
  /** Keep the statement on the address (`pql`). Default true (the item list). */
  syncUrl?: boolean;
  /** Open with this statement in the bar (when the address carries none). */
  initialStatement?: string;
  /** The filters to open with and go back to on Clear. Default: the item list's. */
  initialFilters?: FilterGroup[];
  /** Whose columns these are. Default: the person's item list columns. */
  columns?: ItemQueryColumns;
  /** Rows the host narrows to after the query (a report's range). */
  keepRow?: (row: ItemRow) => boolean;
  /** The items keep the order given (a pool's rank). */
  ranked?: boolean;
}

const NO_SORT: SortSpec[] = [];
const ignoreSort = () => {};

export function useItemQueryWorkspace({
  items,
  syncUrl = true,
  initialStatement,
  initialFilters,
  columns,
  keepRow,
  ranked = false,
}: ItemQueryWorkspaceOptions) {
  const ownedColumns = columns && 'visible' in columns ? columns : null;
  const list = useItemListState({
    initialFilters,
    columns: columns && 'storageKey' in columns ? columns : undefined,
  });
  const allColumns = useAllColumns();

  // A ranked list's order is its rank: an ORDER BY in the statement is not
  // applied there (orderIgnored says so on screen).
  const sort = ranked ? NO_SORT : list.sort;
  const setSort = ranked ? ignoreSort : list.setSort;
  const statement = useStatementQuery({
    filterGroups: list.filterGroups,
    replaceFilters: list.replaceFilters,
    sort,
    setSort,
    syncUrl,
    initialStatement,
  });

  // Statement mode: the applied statement is the query (NOT and nesting
  // included). Builder mode: the filters are. One rule for every host
  // (lib/customReportQuery says the same for the report's print page).
  const queryAst = statement.mode === 'statement' ? statement.appliedAst : undefined;

  const data = useItemListData(
    items,
    list.searchQuery,
    list.filterGroups,
    sort,
    ranked ? null : list.groupBy,
    queryAst,
    list.expandedClusters,
  );

  const rows = useMemo(() => (keepRow ? data.rows.filter(keepRow) : data.rows), [data.rows, keepRow]);
  const displayRows = useMemo(() => {
    // Ranked: every member keeps its own row and place.
    if (ranked) return rows;
    return keepRow ? data.displayRows.filter((r) => r.clusterEntry || keepRow(r)) : data.displayRows;
  }, [ranked, rows, keepRow, data.displayRows]);
  const matchedItemIds = useMemo(() => rows.map((r) => r.id), [rows]);

  // Cluster entries are synthetic rows — selection is translated at the table boundary.
  const selection = useClusterRowSelection({
    displayRows,
    selectedRows: list.selectedRows,
    toggleRowSelection: list.toggleRowSelection,
    setRowsSelected: list.setRowsSelected,
    selectAllRows: list.selectAllRows,
  });

  // Columns: the host's own set, or the person's in this browser.
  const setOwnedColumns = ownedColumns?.setVisible;
  const ownedVisible = ownedColumns?.visible;
  const toggleOwnedColumn = useCallback(
    (key: string) => {
      if (!setOwnedColumns || !ownedVisible) return;
      setOwnedColumns(ownedVisible.includes(key) ? ownedVisible.filter((k) => k !== key) : [...ownedVisible, key]);
    },
    [setOwnedColumns, ownedVisible],
  );
  const visibleColumns = ownedVisible ?? list.visibleColumns;
  const toggleColumn = setOwnedColumns ? toggleOwnedColumn : list.toggleColumn;
  const reorderColumns = setOwnedColumns ?? list.reorderColumns;

  // The Filters panel shows the query as filters. Opened while the bar holds a
  // statement, it takes the statement into the builder (lossless). A statement
  // the builder cannot show (NOT, nesting) stays in the bar, and Filters is not
  // offered for it — never a button that refuses after the click (build
  // contract §2.8). Going back to the statement closes the panel.
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);
  const { mode, canExitToBuilder, exitToBuilder } = statement;
  const filtersOffered = mode === 'builder' || canExitToBuilder;
  const toggleFilters = useCallback(() => {
    if (filterPanelOpen) {
      setFilterPanelOpen(false);
      return;
    }
    if (mode === 'statement' && !exitToBuilder()) return;
    setFilterPanelOpen(true);
  }, [filterPanelOpen, mode, exitToBuilder]);
  useEffect(() => {
    if (filterPanelOpen && mode === 'statement') setFilterPanelOpen(false);
  }, [filterPanelOpen, mode]);

  // Whether a query or a search is narrowing the list right now — a pool
  // with one on is a view of its members, not its order.
  const queryActive =
    list.searchQuery.trim() !== '' ||
    (mode === 'statement' ? statement.appliedAst !== null : list.filterCount > 0);
  const { clear: clearStatement } = statement;
  const { clearFilters, setSearchQuery } = list;
  const clearQuery = useCallback(() => {
    if (mode === 'statement') clearStatement();
    clearFilters();
    setSearchQuery('');
  }, [mode, clearStatement, clearFilters, setSearchQuery]);
  const orderIgnored = ranked && statement.appliedQuery.orderBy.length > 0;

  // Queue names for the Queue column, and the queues its in-place edit offers.
  const orgUnits = useEnterpriseIdentityStore((s) => s.orgUnits);
  const queueNameMap = useMemo(
    () =>
      Object.fromEntries(
        Array.from(orgUnits.values()).map((ou) => [ou.id, ou.displayName?.trim() || ou.name?.trim() || ou.id]),
      ),
    [orgUnits],
  );
  const queues = useMemo(
    () => Array.from(orgUnits.values()).map((ou) => ({ id: ou.id, name: ou.displayName })),
    [orgUnits],
  );

  return {
    ...list,
    ...selection,
    sort,
    setSort,
    statement,
    queryAst,
    ranked,
    rows,
    displayRows,
    grouped: ranked ? null : data.grouped,
    totalCount: data.totalCount,
    filteredCount: rows.length,
    matchedItemIds,
    allColumns,
    visibleColumns,
    toggleColumn,
    reorderColumns,
    filterPanelOpen,
    setFilterPanelOpen,
    filtersOffered,
    toggleFilters,
    queryActive,
    clearQuery,
    orderIgnored,
    queueNameMap,
    queues,
  };
}

export type ItemQueryWorkspaceState = ReturnType<typeof useItemQueryWorkspace>;
